<div class="settings-layout">
  <a routerLink="/dashboard" class="back-link">
    <i class="bi bi-arrow-left"></i> Back to Dashboard
  </a>

  <h1 class="page-title">Settings</h1>
  <p class="page-subtitle">Manage your learning plan, reminders, test defaults, and subject data.</p>

  <div class="panel settings-panel">
    <div *ngIf="isLoading" class="panel-loading">
      <span class="spinner-border spinner-border-sm me-2" role="status"></span>
      Loading...
    </div>

    <ng-container *ngIf="!isLoading">
      <ul class="settings-tabs" role="tablist">
        <li role="presentation">
          <button type="button" class="settings-tab" [class.active]="activeTab === 'learning'" (click)="selectTab('learning')">
            Learning
          </button>
        </li>
        <li role="presentation">
          <button type="button" class="settings-tab" [class.active]="activeTab === 'schedule'" (click)="selectTab('schedule')">
            Schedule
          </button>
        </li>
        <li role="presentation">
          <button type="button" class="settings-tab" [class.active]="activeTab === 'reminders'" (click)="selectTab('reminders')">
            Reminders
          </button>
        </li>
        <li role="presentation">
          <button type="button" class="settings-tab" [class.active]="activeTab === 'tests'" (click)="selectTab('tests')">
            Tests
          </button>
        </li>
        <li role="presentation">
          <button type="button" class="settings-tab" [class.active]="activeTab === 'data'" (click)="selectTab('data')">
            Data
          </button>
        </li>
      </ul>

      <div class="row mb-3">
        <div class="col-md-6">
          <label for="learningGoalCourse" class="form-label">Course</label>
          <select
            class="form-select"
            id="learningGoalCourse"
            [(ngModel)]="learningGoalCourseId"
            (change)="onLearningGoalCourseChange()"
            [ngModelOptions]="{standalone: true}">
            <option [ngValue]="null">Select a course...</option>
            <option *ngFor="let course of learningGoalCourses" [ngValue]="course.id">
              {{ course.name }}
            </option>
          </select>
        </div>
      </div>

      <!-- Learning tab -->
      <div *ngIf="activeTab === 'learning'" class="tab-content">
        <div class="learning-target-section">
          <h5 class="settings-section-title">Learning target</h5>

          <div *ngIf="isLoadingGoal" class="panel-loading py-2">
          <span class="spinner-border spinner-border-sm me-2" role="status"></span>
          Loading target...
        </div>

        <ng-container *ngIf="!isLoadingGoal && goalSetup">
          <div class="goal-summary mb-3">
            <div>
              <span>Total questions</span>
              <strong>{{ goalSetup.totalQuestions }}</strong>
            </div>
            <div>
              <span>Already seen</span>
              <strong>{{ goalSetup.questionsSeen }}</strong>
            </div>
            <div>
              <span>Remaining</span>
              <strong>{{ goalSetup.remainingQuestions }}</strong>
            </div>
          </div>

          <div class="goal-presets mb-3">
            <button
              *ngFor="let preset of goalSetup.presets"
              type="button"
              class="goal-preset"
              [class.goal-preset-selected]="selectedGoalPresetId === preset.id && !customGoalDate"
              (click)="selectGoalPreset(preset.id)">
              <span>{{ preset.label }}</span>
              <strong>{{ preset.dailyQuestions }}/day</strong>
              <small>{{ preset.months }} months</small>
            </button>
          </div>

          <p class="section-help mb-2">Or pick a custom finish date instead of a preset.</p>

          <div class="row mb-3 align-items-end g-3">
            <div class="col-auto">
              <label for="customGoalDate" class="form-label">Custom target date</label>
              <input
                id="customGoalDate"
                class="form-control goal-custom-date-input"
                type="date"
                [(ngModel)]="customGoalDate"
                (ngModelChange)="onCustomGoalDateChange()"
                [ngModelOptions]="{standalone: true}">
            </div>
            @if (retentionFlags.leaderboardEnabled()) {
              <div class="col">
                <div class="form-check mt-4">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="leaderboardOptIn"
                    [(ngModel)]="leaderboardOptIn"
                    [ngModelOptions]="{standalone: true}">
                  <label class="form-check-label" for="leaderboardOptIn">Show me on the public leaderboard</label>
                </div>
                <small class="text-muted d-block mt-1" *ngIf="leaderboardOptIn">
                  Your account name is used on the leaderboard.
                </small>
              </div>
            }
          </div>

          <div class="form-actions d-flex flex-wrap align-items-center gap-2">
            <button
              type="button"
              class="btn btn-primary"
              [disabled]="isSavingGoal || !learningGoalCourseId || !goalTargetDate"
              (click)="saveLearningGoal()">
              <span *ngIf="isSavingGoal" class="spinner-border spinner-border-sm me-2"></span>
              Save learning target
            </button>
            <div *ngIf="goalMessage" class="alert mb-0" [ngClass]="goalMessageType === 'success' ? 'alert-success' : 'alert-danger'">
              {{ goalMessage }}
            </div>
          </div>
        </ng-container>
        </div>
      </div>

      <!-- Schedule tab -->
      <div *ngIf="activeTab === 'schedule'" class="tab-content">
        <h5 class="settings-section-title">Training schedule</h5>
        <p class="section-help">Tune how many days you train and how long each session should be.</p>

        <div class="row g-3 mb-3">
          <div class="col-md-4">
            <label for="weeklyTrainingDays" class="form-label">Training days per week</label>
            <select
              id="weeklyTrainingDays"
              class="form-select"
              [(ngModel)]="weeklyTrainingDays"
              [ngModelOptions]="{standalone: true}">
              <option *ngFor="let day of [1,2,3,4,5,6,7]" [ngValue]="day">{{ day }}</option>
            </select>
          </div>
          <div class="col-md-4">
            <label for="preferredSessionSize" class="form-label">Preferred session size</label>
            <input
              id="preferredSessionSize"
              class="form-control"
              type="number"
              min="5"
              max="100"
              [(ngModel)]="preferredSessionSize"
              [ngModelOptions]="{standalone: true}">
            <small class="form-text text-muted">Questions per Train next session.</small>
          </div>
          <div class="col-md-4">
            <label for="dailyMinutesTarget" class="form-label">Daily minutes target</label>
            <input
              id="dailyMinutesTarget"
              class="form-control"
              type="number"
              min="0"
              max="480"
              [(ngModel)]="dailyMinutesTarget"
              [ngModelOptions]="{standalone: true}">
          </div>
        </div>

        <div class="form-check mb-4">
          <input
            class="form-check-input"
            type="checkbox"
            id="includeWeekends"
            [(ngModel)]="includeWeekends"
            [ngModelOptions]="{standalone: true}">
          <label class="form-check-label" for="includeWeekends">Include weekends in training days</label>
        </div>

        <div class="form-actions d-flex flex-wrap align-items-center gap-2">
          <button
            type="button"
            class="btn btn-primary"
            [disabled]="isSavingSchedule || !learningGoalCourseId"
            (click)="saveScheduleSettings()">
            <span *ngIf="isSavingSchedule" class="spinner-border spinner-border-sm me-2"></span>
            Save schedule
          </button>
          <div *ngIf="scheduleMessage" class="alert mb-0" [ngClass]="scheduleMessageType === 'success' ? 'alert-success' : 'alert-danger'">
            {{ scheduleMessage }}
          </div>
        </div>
      </div>

      <!-- Reminders tab -->
      <div *ngIf="activeTab === 'reminders'" class="tab-content">
        <h5 class="settings-section-title">Reminder timing</h5>
        <p class="section-help">Choose when {{ brand.productName }} nudges you to train and how persistent reminders should be.</p>

        <div class="row g-3 mb-3">
          <div class="col-md-4">
            <label for="reminderTimeLocal" class="form-label">Daily reminder time</label>
            <input
              id="reminderTimeLocal"
              class="form-control"
              type="time"
              [(ngModel)]="reminderTimeLocal"
              [ngModelOptions]="{standalone: true}">
            <small class="form-text text-muted">Uses your device timezone.</small>
          </div>
          <div class="col-md-4">
            <label for="reminderStyle" class="form-label">Reminder style</label>
            <select
              id="reminderStyle"
              class="form-select"
              [(ngModel)]="reminderStyle"
              [ngModelOptions]="{standalone: true}">
              <option value="relaxed">Relaxed</option>
              <option value="normal">Normal</option>
              <option value="strict">Strict</option>
            </select>
            <small class="form-text text-muted">Strict sends streak-save reminders sooner.</small>
          </div>
        </div>

        <h5 class="settings-section-title">Channels</h5>
        <p class="section-help">Reminders appear through Sky coach in the app. Email can be prepared for weekly summaries.</p>

        <div class="form-check mb-2">
          <input class="form-check-input" type="checkbox" id="routineReminderEnabled" [(ngModel)]="routineReminderEnabled" [ngModelOptions]="{standalone: true}">
          <label class="form-check-label" for="routineReminderEnabled">Daily training reminder</label>
        </div>
        <div class="form-check mb-2">
          <input class="form-check-input" type="checkbox" id="saveReminderEnabled" [(ngModel)]="saveReminderEnabled" [ngModelOptions]="{standalone: true}">
          <label class="form-check-label" for="saveReminderEnabled">Streak-save reminder when you fall behind</label>
        </div>
        <div class="form-check mb-3">
          <input class="form-check-input" type="checkbox" id="weeklySummaryEnabled" [(ngModel)]="weeklySummaryEnabled" [ngModelOptions]="{standalone: true}">
          <label class="form-check-label" for="weeklySummaryEnabled">Weekly summary email</label>
        </div>

        <p class="section-help mb-2">Delivery channels</p>
        <div class="channel-checkboxes mb-3">
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="notificationInApp" [(ngModel)]="notificationInApp" [ngModelOptions]="{standalone: true}">
            <label class="form-check-label" for="notificationInApp">Sky coach in-app</label>
          </div>
          <div class="form-check">
            <input class="form-check-input" type="checkbox" id="notificationEmail" [(ngModel)]="notificationEmail" [ngModelOptions]="{standalone: true}">
            <label class="form-check-label" for="notificationEmail">Email summaries</label>
          </div>
        </div>

        <div class="row g-3 mb-4">
          <div class="col-md-4">
            <label for="quietHoursStart" class="form-label">Quiet hours start</label>
            <input id="quietHoursStart" class="form-control" type="time" [(ngModel)]="quietHoursStart" [ngModelOptions]="{standalone: true}">
          </div>
          <div class="col-md-4">
            <label for="quietHoursEnd" class="form-label">Quiet hours end</label>
            <input id="quietHoursEnd" class="form-control" type="time" [(ngModel)]="quietHoursEnd" [ngModelOptions]="{standalone: true}">
          </div>
        </div>

        <div class="form-actions d-flex flex-wrap align-items-center gap-2">
          <button
            type="button"
            class="btn btn-primary"
            [disabled]="isSavingReminders || !learningGoalCourseId"
            (click)="saveReminderSettings()">
            <span *ngIf="isSavingReminders" class="spinner-border spinner-border-sm me-2"></span>
            Save reminders
          </button>
          <div *ngIf="reminderMessage" class="alert mb-0" [ngClass]="reminderMessageType === 'success' ? 'alert-success' : 'alert-danger'">
            {{ reminderMessage }}
          </div>
        </div>
      </div>

      <!-- Tests tab -->
      <div *ngIf="activeTab === 'tests'" class="tab-content">
        <div class="alert alert-info mb-4">
          <i class="bi bi-info-circle"></i> Default test settings apply to all new tests you create.
        </div>

        <form [formGroup]="settingsForm" (ngSubmit)="saveSettings()">
          <div class="row mb-3">
            <div class="col-md-6">
              <label for="seedId" class="form-label">Release Version</label>
              <select class="form-select" id="seedId" formControlName="seedId">
                <option [ngValue]="null">Always latest version</option>
                <option *ngFor="let seed of questionSeeds" [ngValue]="seed.id">
                  {{ seed.name }} ({{ seed.createdAt | date:'short' }})
                </option>
              </select>
              <small class="form-text text-muted">Select a specific release version, or leave as "Always latest version" to use the most recent questions.</small>
            </div>
          </div>

          <div class="mb-4">
            <h5 class="settings-section-title">Default settings for new tests</h5>

            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="shuffleQuestionAnswers" formControlName="shuffleQuestionAnswers">
              <label class="form-check-label" for="shuffleQuestionAnswers">Shuffle question answers</label>
            </div>

            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="randomizeQuestions" formControlName="randomizeQuestions">
              <label class="form-check-label" for="randomizeQuestions">Randomize questions</label>
            </div>

            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="nextAutomatically" formControlName="nextAutomatically">
              <label class="form-check-label" for="nextAutomatically">Jump automatically to next question when correct</label>
            </div>
          </div>

          <div class="mb-4" *ngIf="isNative">
            <h5 class="settings-section-title">Offline learning packs</h5>

            <div class="form-check mb-3">
              <input class="form-check-input" type="checkbox" id="autoDownloadLearningPacks" formControlName="autoDownloadLearningPacks">
              <label class="form-check-label" for="autoDownloadLearningPacks">Download and use offline packs</label>
              <small class="form-text text-muted d-block">When enabled, the app downloads entitled subject packs and studies from the local database when available.</small>
            </div>

            <ng-container *ngIf="settingsForm.get('autoDownloadLearningPacks')?.value">
              <div *ngIf="downloadedPacks.length > 0" class="downloaded-packs-list mb-3">
                <div class="d-flex justify-content-between align-items-baseline mb-1">
                  <small class="form-text text-muted d-block mb-0">Offline packs:</small>
                  <small class="text-muted" *ngIf="totalDownloadedBytes > 0">Total: {{ totalDownloadedSizeLabel }}</small>
                </div>
                <ul class="list-unstyled mb-0">
                  <li *ngFor="let pack of downloadedPacks" class="py-2 border-bottom">
                    <div class="d-flex justify-content-between align-items-center gap-2">
                      <span class="text-truncate">{{ pack.subjectName }}</span>
                      <div class="d-flex align-items-center gap-2 flex-shrink-0">
                        <span [class]="pack.stateBadgeClass">
                          <span *ngIf="pack.state === 'downloading' || pack.state === 'importing'"
                                class="spinner-border spinner-border-sm me-1" style="width: 0.7rem; height: 0.7rem;"></span>
                          {{ pack.stateLabel }}
                        </span>
                        <span *ngIf="pack.sizeLabel" class="text-muted small">{{ pack.sizeLabel }}</span>
                      </div>
                    </div>
                    <div *ngIf="pack.state === 'failed' && pack.lastError" class="small text-danger mt-1">
                      {{ pack.lastError }}
                      <span *ngIf="pack.attemptCount > 0" class="text-muted">(attempts: {{ pack.attemptCount }})</span>
                    </div>
                  </li>
                </ul>
              </div>

              <div *ngIf="downloadedPacks.length === 0 && !isLoadingDownloadedPacks" class="text-muted small mb-3">
                No offline packs downloaded yet.
              </div>

              <div class="form-actions d-flex flex-wrap align-items-center gap-2">
                <button *ngIf="hasFailedPacks" type="button" class="btn btn-outline-secondary"
                        [disabled]="isRetryingDownloads"
                        (click)="retryFailedPacks()">
                  <span *ngIf="isRetryingDownloads" class="spinner-border spinner-border-sm me-2"></span>
                  Retry download
                </button>
                <button type="button" class="btn btn-outline-danger"
                        [disabled]="isClearingDownloads || !hasInstalledPacks"
                        (click)="requestClearDownloadedPacks()">
                  <span *ngIf="isClearingDownloads" class="spinner-border spinner-border-sm me-2"></span>
                  Clear downloaded packs
                </button>
                <div *ngIf="offlinePackMessage" class="alert mb-0" [ngClass]="offlinePackMessageType === 'success' ? 'alert-success' : 'alert-danger'">
                  {{ offlinePackMessage }}
                </div>
              </div>
            </ng-container>
          </div>

          <div class="form-actions mb-4" *ngIf="!isNative">
            <button type="submit" class="btn btn-primary" [disabled]="isSaving || settingsForm.invalid">
              <span *ngIf="isSaving" class="spinner-border spinner-border-sm me-2"></span>
              Save test defaults
            </button>
            <div *ngIf="message" class="alert" [ngClass]="messageType === 'success' ? 'alert-success' : 'alert-danger'">
              {{ message }}
            </div>
          </div>
          <div class="form-actions mb-4" *ngIf="isNative && message">
            <div class="alert" [ngClass]="messageType === 'success' ? 'alert-success' : 'alert-danger'">
              {{ message }}
            </div>
          </div>
        </form>
      </div>

      <!-- Data tab -->
      <div *ngIf="activeTab === 'data'" class="tab-content">
        <h5 class="settings-section-title">Reset whole subject</h5>
        <div class="alert alert-warning">
          <i class="bi bi-exclamation-triangle"></i> This deletes all progress (flags, notes, seen status) for the selected subject. This action cannot be undone.
        </div>

        <div class="row mb-3">
          <div class="col-md-6">
            <label for="resetCourse" class="form-label">Course</label>
            <select class="form-select" id="resetCourse" [(ngModel)]="selectedCourseId" (change)="onCourseChange()" [ngModelOptions]="{standalone: true}">
              <option [ngValue]="null">Select a course...</option>
              <option *ngFor="let course of courses" [ngValue]="course.id">
                {{ course.name }}
              </option>
            </select>
          </div>
          <div class="col-md-6">
            <label for="resetSubject" class="form-label">Subject</label>
            <select class="form-select" id="resetSubject" [(ngModel)]="selectedSubjectId" [ngModelOptions]="{standalone: true}" [disabled]="!selectedCourseId || subjects.length === 0">
              <option [ngValue]="null">Select a subject...</option>
              <option *ngFor="let subject of subjects" [ngValue]="subject.id">
                {{ subject.name }}
              </option>
            </select>
          </div>
        </div>

        <div class="form-actions d-flex flex-wrap align-items-center gap-2">
          <button type="button" class="btn btn-danger"
                  [disabled]="!selectedCourseId || !selectedSubjectId || isResetting"
                  (click)="showResetConfirmDialog()">
            <span *ngIf="isResetting" class="spinner-border spinner-border-sm me-2"></span>
            Reset
          </button>
          <div *ngIf="resetMessage" class="alert mb-0" [ngClass]="resetMessageType === 'success' ? 'alert-success' : 'alert-danger'">
            {{ resetMessage }}
          </div>
        </div>
      </div>
    </ng-container>
  </div>
</div>

<!-- Clear Downloaded Packs Confirmation Modal -->
<div *ngIf="showClearConfirm" class="modal-overlay" (click)="cancelClearDownloads()">
  <div class="modal-dialog modal-dialog-wide" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Clear downloaded packs?</h5>
        <button type="button" class="btn-close" (click)="cancelClearDownloads()"></button>
      </div>
      <div class="modal-body">
        <p>This removes all offline packs from this device. You can re-download them when online.</p>
        <ul *ngIf="hasInstalledPacks">
          <li *ngFor="let pack of downloadedPacks">
            <ng-container *ngIf="pack.isInstalled">
              {{ pack.subjectName }} <span class="text-muted">({{ pack.sizeLabel }})</span>
            </ng-container>
          </li>
        </ul>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="cancelClearDownloads()">Cancel</button>
        <button type="button" class="btn btn-danger" (click)="confirmClearDownloads()">Clear packs</button>
      </div>
    </div>
  </div>
</div>

<!-- Reset Confirmation Modal -->
<div *ngIf="showResetConfirm" class="modal-overlay" (click)="cancelReset()">
  <div class="modal-dialog modal-dialog-wide" (click)="$event.stopPropagation()">
    <div class="modal-content">
      <div class="modal-header">
        <h5 class="modal-title">Confirm reset</h5>
        <button type="button" class="btn-close" (click)="cancelReset()"></button>
      </div>
      <div class="modal-body">
        <p>Are you sure you want to reset the subject <strong>{{ getSelectedSubjectName() }}</strong>?</p>
        <p class="text-danger">This will permanently delete all your progress for this subject, including:</p>
        <ul>
          <li>Question flags</li>
          <li>Notes</li>
          <li>Seen status</li>
        </ul>
        <p class="text-danger"><strong>This action cannot be undone.</strong></p>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-secondary" (click)="cancelReset()">Cancel</button>
        <button type="button" class="btn btn-danger" (click)="confirmReset()">Confirm reset</button>
      </div>
    </div>
  </div>
</div>
